import React from "react";
import { useSelector } from "react-redux";
import PropTypes from "prop-types";
import { FormHelperText, Box, Tooltip } from "@mui/material";
import { createFilterOptions } from "@mui/material/Autocomplete";

import messages from "../../../assets/locale/messages";
import { clx } from "../../../utils/Helpers";
import { UICheckbox, UISelect } from "../../ui";
import "./FilterInput.scss";

const muiFilterOptions = createFilterOptions();

const defaultFilterOptions = (options, state) =>
  muiFilterOptions(options, {
    ...state,
    inputValue: state.inputValue ?? "",
    getOptionLabel: (option) => {
      const label = state.getOptionLabel(option);
      return label == null ? "" : String(label);
    },
  });

const FilterInput = ({
  value,
  inputClassName,
  disabled,
  options,
  placeholder,
  id,
  name,
  onChange,
  helperText,
  helperTextClass,
  filterWrapperClass,
  isMultiple,
  isOptionsElt,
  showOptionTooltip,
  loading,
  inputValue,
  onInputChange,
  open,
  onOpen,
  onClose,
  filterOptions,
}) => {
  const lang = useSelector((state) => state.locale.lang);
  const { shared } = messages[lang];

  const optionTooltipTitle = (option) =>
    typeof option?.name === "string" ? option.name : "";

  const renderOptionContent = (option) =>
    isOptionsElt ? (
      <>
        {option.icon}
        <span className="mx-1">{option?.name}</span>
      </>
    ) : (
      option.name
    );

  const renderTooltipOptionContent = (option) =>
    isOptionsElt ? (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          minWidth: 0,
          width: "100%",
          overflow: "hidden",
        }}
      >
        {option.icon}
        <Box
          component="span"
          className="mx-1"
          sx={{
            minWidth: 0,
            flex: 1,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {option?.name}
        </Box>
      </Box>
    ) : (
      <Box
        component="span"
        sx={{
          display: "block",
          maxWidth: "100%",
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}
      >
        {option?.name}
      </Box>
    );

  // The selection re-read from `options` by id, so its label follows `lang`
  // once the options come back translated.
  const findOption = (selected) =>
    options?.find((option) => option?.id == selected?.id) ?? selected;
  const resolvedValue = isMultiple
    ? value?.map(findOption)
    : value && findOption(value);

  const selectedIcon =
    !isMultiple && isOptionsElt && resolvedValue?.icon
      ? resolvedValue.icon
      : null;
  const wrapperClassName = clx(
    "filter-wrapper",
    filterWrapperClass,
    selectedIcon && "filter-wrapper--has-selected-icon",
  );
  const hasMultipleValue = isMultiple && resolvedValue?.length > 0;

  return (
    <div className={wrapperClassName}>
      <UISelect
        name={name}
        id={id || name}
        options={options || []}
        disabled={disabled}
        multiple={isMultiple}
        // Multi-select: the picked options read as one comma-separated line
        // (same as UISelect's multi usage elsewhere), replacing the placeholder.
        placeholder={hasMultipleValue ? undefined : placeholder}
        renderTags={(selected) => (
          <span className="filter-wrapper__tags">
            {selected.map((option) => option?.name).filter(Boolean).join(", ")}
          </span>
        )}
        // Multi-select: stay open while picking; each option carries a checkbox
        // so the selection is visible and a single option can be unticked.
        disableCloseOnSelect={isMultiple}
        getOptionLabel={(option) => option?.name || ""}
        renderOption={(props, option, { selected }) => {
          // Display only — the whole option row handles the toggle.
          const checkbox = isMultiple ? (
            <span className="filter-input__option-checkbox" aria-hidden="true">
              <UICheckbox checked={selected} label="" tabIndex={-1} />
            </span>
          ) : null;

          if (!showOptionTooltip) {
            return (
              <Box component="li" key={option?.id} {...props}>
                {checkbox}
                {renderOptionContent(option)}
              </Box>
            );
          }

          const title = optionTooltipTitle(option);

          return (
            <li {...props}>
              {checkbox}
              <Tooltip
                title={title}
                placement="top"
                disableInteractive
                enterDelay={400}
                disableHoverListener={!title}
              >
                <Box sx={{ width: "100%", minWidth: 0 }}>
                  {renderTooltipOptionContent(option)}
                </Box>
              </Tooltip>
            </li>
          );
        }}
        isOptionEqualToValue={(option, value) => option?.id == value?.id}
        onChange={(value) => {
          onChange(value);
        }}
        className={clx("filter-input", inputClassName)}
        value={resolvedValue}
        loading={loading}
        inputValue={inputValue}
        onInputChange={onInputChange}
        open={open}
        onOpen={onOpen}
        onClose={onClose}
        filterOptions={filterOptions ?? defaultFilterOptions}
        noOptionsText={shared.noOptions}
        textFieldProps={{
          color: "primary",
          className: "unify-input-padding",
          // Only override `startAdornment` when there's an icon to show — an
          // explicit `undefined` would wipe out the Autocomplete's own adornment,
          // which is where a multi-select renders its selected chips.
          InputProps: selectedIcon
            ? {
                startAdornment: (
                  <Box
                    component="span"
                    className="filter-wrapper__selected-icon"
                  >
                    {selectedIcon}
                  </Box>
                ),
              }
            : undefined,
        }}
      />
      {helperText && (
        <FormHelperText className={helperTextClass || "font-medium fsize-12"}>
          {helperText}
        </FormHelperText>
      )}
    </div>
  );
};

export default FilterInput;

FilterInput.propTypes = {
  disabled: PropTypes.bool,
  onChange: PropTypes.func.isRequired,
  inputClassName: PropTypes.string,
  filterWrapperClass: PropTypes.string,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      name: PropTypes.oneOfType([PropTypes.string, PropTypes.element])
        .isRequired,
      value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      icon: PropTypes.element,
    })
  ),
  placeholder: PropTypes.string,
  label: PropTypes.string,
  name: PropTypes.string.isRequired,
  id: PropTypes.string,
  helperText: PropTypes.oneOfType([PropTypes.string, PropTypes.element]),
  helperTextClass: PropTypes.string,
  value: PropTypes.any,
  isMultiple: PropTypes.bool,
  isOptionsElt: PropTypes.bool,
  showOptionTooltip: PropTypes.bool,
  loading: PropTypes.bool,
  inputValue: PropTypes.string,
  onInputChange: PropTypes.func,
  open: PropTypes.bool,
  onOpen: PropTypes.func,
  onClose: PropTypes.func,
  filterOptions: PropTypes.func,
  showLabel: PropTypes.bool,
};

FilterInput.defaultProps = {
  showOptionTooltip: false,
  loading: false,
  inputValue: undefined,
  onInputChange: undefined,
  open: undefined,
  onOpen: undefined,
  onClose: undefined,
  filterOptions: undefined,
  showLabel: false,
};
